import { useCallback } from 'react';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBilling } from '@/modules/billing/hooks/useBilling';
import { Address } from '@/modules/billing/model';
import { countryListAllIsoData } from '@/util/countries';
import { isRequired } from '@/util/validation/constants';

const countryOptions = countryListAllIsoData.map((c) => ({ label: c.name, value: c.code }));

type FormValues = Address;
const FORM_ID = 'billing-address-form';

export const BillingAddressFieldset = () => {
  const { t } = useTranslation();
  const { canWriteBilling, customer, updateCustomer, updateCustomerStatus } = useBilling();

  const form = useForm<FormValues>({
    mode: 'onChange',
    values: {
      line1: customer?.address?.line1 ?? '',
      line2: customer?.address?.line2 ?? '',
      city: customer?.address?.city ?? '',
      state: customer?.address?.state ?? '',
      postal_code: customer?.address?.postal_code ?? '',
      country: customer?.address?.country ?? 'GR',
    },
  });
  const {
    handleSubmit,
    control,
    formState: { isDirty },
  } = form;

  const onSubmit = useCallback(
    (data: FormValues) => {
      const { line2, ...rest } = data;
      return updateCustomer({ address: { ...rest, ...(line2 ? { line2 } : {}) } });
    },
    [updateCustomer]
  );

  return (
    <div className="flex flex-col gap-2">
      <h2 className="px-1.5 text-sm font-medium text-muted-foreground">
        {t('billing.address_title')}
      </h2>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} id={FORM_ID}>
          <FieldsetCard
            isDirty={isDirty}
            isLoading={updateCustomerStatus === 'pending'}
            disabled={!isDirty}
            saveButtonLabel={t('common.save')}
            formId={FORM_ID}
          >
            <div className="flex flex-col gap-4">
              <FormField
                control={control}
                name="line1"
                rules={isRequired}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('field.address')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('billing.address_line_1_placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="line2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('billing.address_line2')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('billing.address_line_2_placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('billing.city')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('billing.city_placeholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('billing.state')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('billing.state_placeholder')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name="postal_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('billing.zipcode')}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('field.country')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={!canWriteBilling}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {countryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FieldsetCard>
        </form>
      </Form>
    </div>
  );
};
